import { inlineEditorHandler } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorHandler';
import { PointerAutoComplete } from '@/app/gridGL/interaction/pointer/PointerAutoComplete';
import { PointerCellMoving } from '@/app/gridGL/interaction/pointer/PointerCellMoving';
import { PointerCursor } from '@/app/gridGL/interaction/pointer/pointerCursor';
import { PointerDown } from '@/app/gridGL/interaction/pointer/PointerDown';
import { PointerHeading } from '@/app/gridGL/interaction/pointer/PointerHeading';
import { PointerHtmlCells } from '@/app/gridGL/interaction/pointer/PointerHtmlCells';
import { PointerImages } from '@/app/gridGL/interaction/pointer/PointerImages';
import { PointerLink } from '@/app/gridGL/interaction/pointer/PointerLink';
import { PointerTable } from '@/app/gridGL/interaction/pointer/PointerTable';
import { PointerTableResize } from '@/app/gridGL/interaction/pointer/PointerTableResize';
import { frozenPanes } from '@/app/gridGL/pixiApp/FrozenPanes';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { multiplayer } from '@/app/web-workers/multiplayerWebWorker/multiplayer';
import type { Viewport } from 'pixi-viewport';
import type { FederatedPointerEvent } from 'pixi.js';

export class Pointer {
  pointerHeading: PointerHeading;
  private pointerImages: PointerImages;
  pointerAutoComplete: PointerAutoComplete;
  pointerTableResize: PointerTableResize;
  private pointerHtmlCells: PointerHtmlCells;
  private pointerCursor: PointerCursor;
  pointerDown: PointerDown;
  pointerCellMoving: PointerCellMoving;
  private pointerTable: PointerTable;
  private pointerLink: PointerLink;

  constructor(viewport: Viewport) {
    this.pointerHeading = new PointerHeading();
    this.pointerAutoComplete = new PointerAutoComplete();
    this.pointerTableResize = new PointerTableResize();
    this.pointerImages = new PointerImages();
    this.pointerDown = new PointerDown();
    this.pointerCursor = new PointerCursor();
    this.pointerHtmlCells = new PointerHtmlCells();
    this.pointerCellMoving = new PointerCellMoving();
    this.pointerTable = new PointerTable();
    this.pointerLink = new PointerLink();

    viewport.on('pointerdown', this.handlePointerDown);
    viewport.on('pointermove', this.pointerMove);
    viewport.on('pointerup', this.pointerUp);
    viewport.on('pointerupoutside', this.pointerUp);

    // Cancellation triggers are scoped to "the drag actually left the app" or
    // "the browser told us to cancel" - NOT to "the cursor moved off the
    // canvas DOM rect". Listening on the canvas's pointerleave would cancel
    // any drag whose cursor wandered onto an in-app DOM element such as the
    // toolbar, sheet bar, code editor, or AI sidebar - all of which are
    // legitimate things to overshoot during a selection or table move.
    if (pixiApp.canvas) {
      pixiApp.canvas.addEventListener('pointercancel', this.cancelAllPointerGestures);
    }
    document.documentElement.addEventListener('mouseleave', this.handleDocumentMouseLeave);
    document.documentElement.addEventListener('mouseenter', this.handleDocumentMouseEnter);
    document.documentElement.addEventListener('mousemove', this.handleDocumentMouseMove);
    window.addEventListener('blur', this.handleWindowBlur);
    document.addEventListener('visibilitychange', this.visibilityChange);
    window.addEventListener('mouseup', this.pointerUpOutsideViewport);
  }

  // Returns true if any drag-style gesture is currently in progress that the
  // user would expect to keep going if the cursor wanders off the canvas
  // (e.g., a selection, header range select, autocomplete drag, cell/table
  // move, or image resize).
  private isAnyDragActive = (): boolean => {
    return (
      this.pointerDown.active ||
      // A freeze-handle drag is a short, local gesture committed by pixi's own
      // pointerup/pointerupoutside. Excluding it here keeps the document-level
      // mouse handlers (which treat a buttons===0 mousemove as a "missed
      // mouseup") from cancelling it mid-release and discarding the drop.
      (this.pointerHeading.active && !this.pointerHeading.draggingFreeze) ||
      this.pointerAutoComplete.active ||
      this.pointerCellMoving.state === 'move' ||
      !!this.pointerImages.resizing
    );
  };

  // While the cursor is outside the canvas during a drag (over the toolbar,
  // sheet bar, code editor, AI sidebar, or fully off the browser window),
  // drive viewport auto-scroll from a requestAnimationFrame loop instead of
  // the pixi-viewport MouseEdges plugin. MouseEdges only updates its scroll
  // velocity from pointer events on the canvas, which stop firing the
  // moment the cursor crosses out of the canvas DOM rect - so on its own it
  // would stop scrolling as soon as the cursor reached the toolbar.
  private offCanvasCursorPos?: { x: number; y: number };
  private offCanvasRaf?: number;

  // Canvas rect cache. `getBoundingClientRect` is called from
  // `handleDocumentMouseMove`, which fires on every native mousemove
  // anywhere on the page (including hundreds per second on a fast drag).
  // When React state updates from `selectTo` invalidate page layout each
  // frame, every uncached `getBoundingClientRect` forces a synchronous
  // layout recalculation - which is what causes the page to freeze when
  // dragging a selection rapidly. Cache the rect for one frame's worth
  // of mouse events; staleness here only affects the on/off-canvas
  // classification by ~16ms, which is imperceptible.
  private cachedCanvasRect?: DOMRect;
  private cachedCanvasRectAt = 0;
  private static readonly CANVAS_RECT_TTL_MS = 16;

  private getCanvasRect = (): DOMRect | undefined => {
    const canvas = pixiApp.canvas;
    if (!canvas) return undefined;
    const now = performance.now();
    if (!this.cachedCanvasRect || now - this.cachedCanvasRectAt > Pointer.CANVAS_RECT_TTL_MS) {
      this.cachedCanvasRect = canvas.getBoundingClientRect();
      this.cachedCanvasRectAt = now;
    }
    return this.cachedCanvasRect;
  };

  private isCursorOverCanvas = (clientX: number, clientY: number): boolean => {
    const rect = this.getCanvasRect();
    if (!rect) return false;
    return clientX >= rect.left && clientX < rect.right && clientY >= rect.top && clientY < rect.bottom;
  };

  private handleDocumentMouseMove = (e: MouseEvent) => {
    if (!this.isAnyDragActive()) return;
    // A buttons-less mousemove means the button was already released. The
    // browser does not deliver mouseup events that happen outside the window
    // (e.g., the user released the mouse over the OS chrome above the title
    // bar), so this is the earliest reliable signal that such an off-window
    // drag is over - cancel it here so the auto-scroll RAF loop stops and the
    // gesture doesn't stay "stuck". We must only do this when the cursor is
    // off the canvas: when the release happens over the canvas, PixiJS will
    // deliver its own pointerup which finalizes (commits) the gesture.
    // Cancelling here would race that pointerup and revert the gesture
    // instead - e.g. a column/row resize snaps back to its original size when
    // the button is released very quickly while the cursor is still moving,
    // because a buttons-less mousemove can arrive before pixi's pointerup.
    if (e.buttons === 0) {
      // A resize released off-canvas should commit at its dragged size, not
      // revert; everything else is cancelled.
      if (!this.isCursorOverCanvas(e.clientX, e.clientY) && !this.commitOffCanvasGestures()) {
        this.cancelAllPointerGestures();
      }
      return;
    }
    if (this.isCursorOverCanvas(e.clientX, e.clientY)) {
      this.stopOffCanvasAutoScroll();
      return;
    }
    this.offCanvasCursorPos = { x: e.clientX, y: e.clientY };
    this.startOffCanvasAutoScroll();
  };

  private handleDocumentMouseLeave = (e: MouseEvent) => {
    if (!this.isAnyDragActive() || e.buttons === 0) {
      // A resize whose button was already released as the cursor left the
      // document should commit at its dragged size rather than revert.
      if (e.buttons === 0 && this.commitOffCanvasGestures()) {
        return;
      }
      this.cancelAllPointerGestures();
      return;
    }
    // The browser stops firing mousemove once the cursor exits the document,
    // so seed the auto-scroll loop with the exit position to keep scrolling
    // in the direction the cursor went.
    this.offCanvasCursorPos = { x: e.clientX, y: e.clientY };
    this.startOffCanvasAutoScroll();
  };

  private handleDocumentMouseEnter = (e: MouseEvent) => {
    // The browser does not deliver mouseup events that occur outside the
    // window, so a drag that ended off-screen would otherwise stay "stuck"
    // until the user clicks again. When the cursor returns with no buttons
    // pressed, treat it as the missed release.
    if (e.buttons === 0 && this.isAnyDragActive()) {
      this.cancelAllPointerGestures();
      return;
    }
    // If the cursor came back over the canvas itself, the regular pointer
    // pipeline will resume; the auto-scroll loop is no longer needed. (If
    // the cursor came back onto an in-app DOM element like the toolbar, the
    // mousemove handler above will keep the loop running.)
    if (this.isCursorOverCanvas(e.clientX, e.clientY)) {
      this.stopOffCanvasAutoScroll();
    }
  };

  private startOffCanvasAutoScroll = () => {
    if (this.offCanvasRaf !== undefined || !this.offCanvasCursorPos) return;
    // Disable MouseEdges so it doesn't fight our manual scroll loop. The
    // active gesture's `pointerMove` handler will re-add the plugin the
    // moment the cursor returns to the canvas.
    pixiApp.viewport.disableMouseEdges();

    const tick = () => {
      this.offCanvasRaf = undefined;
      if (!this.offCanvasCursorPos || !this.isAnyDragActive()) {
        this.offCanvasCursorPos = undefined;
        return;
      }
      // A heading resize tracks the cursor but must not edge-scroll (see
      // `scrollViewportFromOffCanvasCursor`); every other drag may scroll.
      pixiApp.viewport.scrollViewportFromOffCanvasCursor(
        this.offCanvasCursorPos.x,
        this.offCanvasCursorPos.y,
        !this.pointerHeading.active
      );
      // The cascade fired by the scroll above can synchronously cancel the
      // gesture (e.g., a handler decided the move target is invalid). Skip
      // re-scheduling in that case so we don't keep churning frames after
      // the drag is gone.
      if (!this.offCanvasCursorPos || !this.isAnyDragActive()) {
        this.offCanvasCursorPos = undefined;
        return;
      }
      this.offCanvasRaf = requestAnimationFrame(tick);
    };
    this.offCanvasRaf = requestAnimationFrame(tick);
  };

  private stopOffCanvasAutoScroll = () => {
    if (this.offCanvasRaf !== undefined) {
      cancelAnimationFrame(this.offCanvasRaf);
      this.offCanvasRaf = undefined;
    }
    this.offCanvasCursorPos = undefined;
  };

  // Finalize (commit) a heading resize whose pointer was released while the
  // cursor was off the canvas - e.g. the user dragged a column/row border up
  // onto the toolbar and let go there. PixiJS only delivers a pointerup for
  // in-canvas releases, so without this the release would fall through to
  // `cancelAllPointerGestures`, whose `handleEscape` reverts the resize back to
  // its original size. Returns true when it handled an active resize.
  private commitOffCanvasGestures = (): boolean => {
    if (this.pointerHeading.active) {
      this.stopOffCanvasAutoScroll();
      this.pointerHeading.pointerUp();
      this.updateCursor();
      return true;
    }
    return false;
  };

  private visibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      multiplayer.sendMouseMove();
      this.cancelAllPointerGestures();
    }
  };

  private handleWindowBlur = () => {
    multiplayer.sendMouseMove();
    this.cancelAllPointerGestures();
  };

  private pointerUpOutsideViewport = (e: MouseEvent) => {
    // Only run the defensive cancel when mouseup happened outside the canvas;
    // in-canvas releases are already handled by the viewport's pointerup
    // listener and re-cancelling here would clobber legitimate gestures.
    const target = e.target;
    const isOutsideCanvas = !(target instanceof Node) || (!!pixiApp.canvas && !pixiApp.canvas.contains(target));
    if (isOutsideCanvas) {
      // A resize released over the toolbar (or any off-canvas DOM) should
      // commit at its dragged size rather than revert.
      if (!this.commitOffCanvasGestures()) {
        this.cancelAllPointerGestures();
      }
    }
    if (
      target instanceof HTMLElement &&
      // A mouseup whose target was removed from the DOM between mousedown and
      // mouseup (e.g. the inline editor's transient "return to cell" label,
      // which React unmounts during the interaction) is not a click on some
      // element outside the editor, so it must not commit/close the editor.
      target.isConnected &&
      !target.closest('.pointer-up-ignore') &&
      !inlineEditorHandler.containsElement(target)
    ) {
      inlineEditorHandler.close({ skipFocusGrid: true });
    }
  };

  destroy() {
    const viewport = pixiApp.viewport;
    viewport.off('pointerdown', this.handlePointerDown);
    viewport.off('pointermove', this.pointerMove);
    viewport.off('pointerup', this.pointerUp);
    viewport.off('pointerupoutside', this.pointerUp);
    pixiApp.canvas?.removeEventListener('pointercancel', this.cancelAllPointerGestures);
    document.documentElement.removeEventListener('mouseleave', this.handleDocumentMouseLeave);
    document.documentElement.removeEventListener('mouseenter', this.handleDocumentMouseEnter);
    document.documentElement.removeEventListener('mousemove', this.handleDocumentMouseMove);
    window.removeEventListener('blur', this.handleWindowBlur);
    document.removeEventListener('visibilitychange', this.visibilityChange);
    window.removeEventListener('mouseup', this.pointerUpOutsideViewport);
    this.stopOffCanvasAutoScroll();
    this.pointerDown.destroy();
    this.pointerHtmlCells.destroy();
    this.pointerImages.destroy();
  }

  // check if more than one touch point (let the viewport handle the event)
  private isMoreThanOneTouch(e: FederatedPointerEvent): boolean {
    return ((e.nativeEvent as any)?.touches?.length ?? 0) > 1;
  }

  // todo: this should be removed when the code editor's layout is changed
  private isOverCodeEditor(e: FederatedPointerEvent): boolean {
    const codeEditor = document.getElementById('QuadraticCodeEditorID');
    const overCodeEditor = !!codeEditor?.matches(':hover');
    if (!overCodeEditor) {
      multiplayer.sendMouseMove();
    }
    return overCodeEditor;
  }

  private handlePointerDown = (e: FederatedPointerEvent) => {
    if (pixiApp.loading) return;
    if (this.isMoreThanOneTouch(e)) return;
    const world = frozenPanes.getFrozenWorld(e.global, pixiApp.viewport.toWorld(e.global));

    // the pointerImage.resizing check is needed so pointerHtmlCells
    // do not interfere with pointerImages when its resizing.
    this.pointerHeading.pointerDown(world, e) ||
      (!this.pointerImages.resizing && this.pointerHtmlCells.pointerDown(e)) ||
      this.pointerImages.pointerDown(world, e) ||
      this.pointerTableResize.pointerDown(world) ||
      this.pointerCellMoving.pointerDown(e) ||
      this.pointerHtmlCells.pointerDown(e) ||
      this.pointerLink.pointerDown(world, e) ||
      this.pointerTable.pointerDown(world, e) ||
      this.pointerAutoComplete.pointerDown(world) ||
      this.pointerDown.pointerDown(world, e);

    this.updateCursor();
  };

  pointerMove = (e: FederatedPointerEvent) => {
    if (pixiApp.loading) return;

    // ignore pointerMove if the target is a child of an element with class pointer-move-ignore
    const target = e.originalEvent?.target;
    if (target) {
      const isWithinPointerMoveIgnore = target instanceof HTMLElement && !!target.closest('.pointer-move-ignore');
      if (isWithinPointerMoveIgnore) return;
    }

    if (this.isMoreThanOneTouch(e) || this.isOverCodeEditor(e)) return;

    const world = frozenPanes.getFrozenWorld(e.global, pixiApp.viewport.toWorld(e.global));

    // While dragging a freeze handle, route the move exclusively to the
    // heading handler. Otherwise an earlier handler in the chain (e.g.,
    // pointerHtmlCells or pointerCellMoving) can consume the move and the
    // freeze preview never updates.
    if (this.pointerHeading.draggingFreeze) {
      this.pointerHeading.pointerMove(world, e);
      this.updateCursor();
      return;
    }

    // the pointerImage.resizing check is needed so pointerHtmlCells
    // do not interfere with pointerImages when its resizing.
    (!this.pointerImages.resizing && this.pointerHtmlCells.pointerMove(world, e)) ||
      this.pointerImages.pointerMove(world) ||
      this.pointerTableResize.pointerMove(world) ||
      this.pointerCellMoving.pointerMove(world, e) ||
      this.pointerHtmlCells.pointerMove(world, e) ||
      // we need the pointerDown.active and pointerHeading.active check to
      // ensure that when dragging the mouse for selection, we don't call
      // select_table
      (!this.pointerDown.active && !this.pointerHeading.active && this.pointerTable.pointerMove(world)) ||
      this.pointerHeading.pointerMove(world, e) ||
      this.pointerAutoComplete.pointerMove(world) ||
      this.pointerDown.pointerMove(world, e) ||
      this.pointerCursor.pointerMove(world) ||
      this.pointerLink.pointerMove(world, e);

    this.updateCursor();
  };

  // change the cursor based on pointer priority
  private updateCursor() {
    const cursor =
      this.pointerTableResize.cursor ??
      this.pointerCellMoving.cursor ??
      this.pointerHtmlCells.cursor ??
      this.pointerImages.cursor ??
      this.pointerHeading.cursor ??
      this.pointerAutoComplete.cursor ??
      this.pointerLink.cursor ??
      this.pointerTable.cursor;

    pixiApp.canvas.style.cursor = cursor ?? 'unset';
  }

  private pointerUp = (e: FederatedPointerEvent) => {
    if (pixiApp.loading) return;
    if (this.isMoreThanOneTouch(e)) {
      this.cancelAllPointerGestures();
      return;
    }

    // While dragging a freeze handle, commit via the heading handler first.
    // Otherwise an earlier handler in the chain can return true and skip the
    // freeze commit, leaving freezeDragging stuck until the next drag.
    if (this.pointerHeading.draggingFreeze) {
      this.pointerHeading.pointerUp();
      this.updateCursor();
      return;
    }

    this.pointerHtmlCells.pointerUp(e) ||
      this.pointerImages.pointerUp() ||
      this.pointerTableResize.pointerUp() ||
      this.pointerCellMoving.pointerUp() ||
      this.pointerTable.pointerUp() ||
      this.pointerHeading.pointerUp() ||
      this.pointerAutoComplete.pointerUp() ||
      this.pointerDown.pointerUp(e);

    this.updateCursor();
  };

  handleEscape(): boolean {
    // close search if canvas gets the keyboard event
    if (gridSettings.getEditorInteractionState().showSearch) {
      gridSettings.setEditorInteractionState({
        ...gridSettings.getEditorInteractionState(),
        showSearch: false,
      });
      return true;
    }
    return (
      this.pointerTableResize.handleEscape() ||
      this.pointerCellMoving.handleEscape() ||
      this.pointerHtmlCells.handleEscape() ||
      this.pointerImages.handleEscape() ||
      this.pointerHeading.handleEscape() ||
      this.pointerAutoComplete.handleEscape()
    );
  }

  cancelAllPointerGestures = (): void => {
    this.stopOffCanvasAutoScroll();
    this.pointerCellMoving.handleEscape();
    this.pointerHtmlCells.handleEscape();
    this.pointerImages.handleEscape();
    this.pointerHeading.handleEscape();
    this.pointerAutoComplete.handleEscape();
    this.pointerTableResize.handleEscape();
    this.pointerDown.pointerUp();
    this.updateCursor();
  };

  getCursor(): string {
    return this.pointerHeading.cursor || this.pointerAutoComplete.cursor || this.pointerTableResize.cursor || 'default';
  }
}
